/**
 * 用户信息服务 - 核心层（框架无关）
 * 负责用户信息的获取、缓存和管理
 */

export interface UserProfile {
  id: string
  tenantId: string
  industryCode: string
  role: string
  username: string
  email?: string
  phone?: string
  avatar?: string
  permissions: string[]
  preferences?: UserPreferences
  metadata?: Record<string, any>
  createdAt?: Date
  updatedAt?: Date
}

export interface UserPreferences {
  language?: string
  timezone?: string
  notificationSettings?: NotificationSettings
  uiSettings?: UISettings
  contentPreferences?: ContentPreferences
}

export interface NotificationSettings {
  emailNotifications: boolean
  pushNotifications: boolean
  contentUpdates: boolean
  systemAlerts: boolean
}

export interface UISettings {
  theme: 'light' | 'dark' | 'auto'
  fontSize: 'small' | 'medium' | 'large'
  density: 'compact' | 'normal' | 'comfortable'
  sidebarCollapsed: boolean
}

export interface ContentPreferences {
  defaultContentType: string
  defaultTemplateId?: string
  aiModel: string
  temperature: number
  maxTokens: number
  autoSave: boolean
  qualityCheckEnabled: boolean
}

export interface UserQueryOptions {
  includePermissions?: boolean
  includePreferences?: boolean
  includeMetadata?: boolean
  forceRefresh?: boolean
}

/**
 * 用户信息服务接口
 */
export interface IUserProfileService {
  getCurrentUser(options?: UserQueryOptions): Promise<UserProfile | null>
  getUserById(userId: string, options?: UserQueryOptions): Promise<UserProfile | null>
  updateUserPreferences(preferences: Partial<UserPreferences>): Promise<void>
  clearCache(): void
  hasPermission(permission: string): Promise<boolean>
  getAvailableIndustries(): Promise<string[]>
}

/**
 * 用户信息服务实现
 */
export class UserProfileService implements IUserProfileService {
  private cache: Map<string, { data: UserProfile; timestamp: number }> = new Map()
  private cacheTTL: number = 5 * 60 * 1000 // 5分钟缓存
  
  constructor(
    private userRepository: IUserRepository,
    private permissionService: IPermissionService,
    private cacheService?: ICacheService
  ) {}

  /**
   * 获取当前用户信息
   */
  async getCurrentUser(options: UserQueryOptions = {}): Promise<UserProfile | null> {
    const cacheKey = this.getCacheKey('current', options)
    
    // 检查缓存
    if (!options.forceRefresh) {
      const cached = await this.getFromCache(cacheKey)
      if (cached) {
        return cached
      }
    }
    
    try {
      // 从数据源获取用户信息
      const userData = await this.userRepository.getCurrentUser()
      
      if (!userData) {
        return null
      }
      
      // 构建用户信息
      const userProfile: UserProfile = {
        id: userData.id,
        tenantId: userData.tenantId,
        industryCode: userData.industryCode,
        role: userData.role,
        username: userData.username,
        email: userData.email,
        phone: userData.phone,
        avatar: userData.avatar,
        permissions: [],
        preferences: userData.preferences,
        metadata: userData.metadata,
        createdAt: userData.createdAt,
        updatedAt: userData.updatedAt
      }
      
      // 获取权限信息
      if (options.includePermissions !== false) {
        userProfile.permissions = await this.permissionService.getUserPermissions(userData.id)
      }
      
      // 缓存用户信息
      await this.setToCache(cacheKey, userProfile)
      
      return userProfile
    } catch (error) {
      console.error('获取当前用户信息失败:', error)
      return null
    }
  }

  /**
   * 根据ID获取用户信息
   */
  async getUserById(userId: string, options: UserQueryOptions = {}): Promise<UserProfile | null> {
    const cacheKey = this.getCacheKey(`user_${userId}`, options)
    
    // 检查缓存
    if (!options.forceRefresh) {
      const cached = await this.getFromCache(cacheKey)
      if (cached) {
        return cached
      }
    }
    
    try {
      // 从数据源获取用户信息
      const userData = await this.userRepository.getUserById(userId)
      
      if (!userData) {
        return null
      }
      
      // 构建用户信息
      const userProfile: UserProfile = {
        id: userData.id,
        tenantId: userData.tenantId,
        industryCode: userData.industryCode,
        role: userData.role,
        username: userData.username,
        email: userData.email,
        phone: userData.phone,
        avatar: userData.avatar,
        permissions: [],
        preferences: userData.preferences,
        metadata: userData.metadata,
        createdAt: userData.createdAt,
        updatedAt: userData.updatedAt
      }
      
      // 获取权限信息
      if (options.includePermissions !== false) {
        userProfile.permissions = await this.permissionService.getUserPermissions(userId)
      }
      
      // 缓存用户信息
      await this.setToCache(cacheKey, userProfile)
      
      return userProfile
    } catch (error) {
      console.error('获取用户信息失败:', error)
      return null
    }
  }

  /**
   * 更新用户偏好设置
   */
  async updateUserPreferences(preferences: Partial<UserPreferences>): Promise<void> {
    try {
      await this.userRepository.updateCurrentUserPreferences(preferences)
      
      // 清除当前用户缓存
      await this.clearUserCache('current')
    } catch (error) {
      console.error('更新用户偏好设置失败:', error)
      throw error
    }
  }

  /**
   * 检查用户是否有指定权限
   */
  async hasPermission(permission: string): Promise<boolean> {
    try {
      const userProfile = await this.getCurrentUser({ includePermissions: true })
      
      if (!userProfile) {
        return false
      }
      
      return userProfile.permissions.includes(permission)
    } catch (error) {
      console.error('检查权限失败:', error)
      return false
    }
  }

  /**
   * 获取用户可访问的行业列表
   */
  async getAvailableIndustries(): Promise<string[]> {
    try {
      const userProfile = await this.getCurrentUser({ includePermissions: true })
      
      if (!userProfile) {
        return []
      }
      
      // 从权限中提取行业信息
      const industries = new Set<string>()
      
      userProfile.permissions.forEach(perm => {
        const parts = perm.split(':')
        if (parts.length === 4) {
          const industryCode = parts[0]
          if (industryCode !== '*') {
            industries.add(industryCode)
          }
        }
      })
      
      // 如果用户有通配符权限，返回所有行业
      if (userProfile.permissions.some(perm => perm.startsWith('*:'))) {
        return ['AGR001', 'AUT001', 'EST001', 'EDU001']
      }
      
      return Array.from(industries)
    } catch (error) {
      console.error('获取可用行业列表失败:', error)
      return []
    }
  }

  /**
   * 清除所有缓存
   */
  clearCache(): void {
    this.cache.clear()
    
    if (this.cacheService) {
      this.cacheService.clearByPrefix('user_')
    }
  }

  /**
   * 清除指定用户缓存
   */
  private async clearUserCache(userKey: string): Promise<void> {
    // 清除内存缓存
    for (const [key] of this.cache) {
      if (key.includes(userKey)) {
        this.cache.delete(key)
      }
    }
    
    // 清除外部缓存
    if (this.cacheService) {
      await this.cacheService.clearByPattern(`*${userKey}*`)
    }
  }

  /**
   * 从缓存获取数据
   */
  private async getFromCache(cacheKey: string): Promise<UserProfile | null> {
    // 先检查内存缓存
    const cachedItem = this.cache.get(cacheKey)
    if (cachedItem && Date.now() - cachedItem.timestamp < this.cacheTTL) {
      return cachedItem.data
    }
    
    // 检查外部缓存
    if (this.cacheService) {
      const cachedData = await this.cacheService.get<UserProfile>(cacheKey)
      if (cachedData) {
        // 同步到内存缓存
        this.cache.set(cacheKey, { data: cachedData, timestamp: Date.now() })
        return cachedData
      }
    }
    
    return null
  }

  /**
   * 设置缓存数据
   */
  private async setToCache(cacheKey: string, data: UserProfile): Promise<void> {
    const cacheItem = { data, timestamp: Date.now() }
    
    // 设置内存缓存
    this.cache.set(cacheKey, cacheItem)
    
    // 设置外部缓存
    if (this.cacheService) {
      await this.cacheService.set(cacheKey, data, this.cacheTTL)
    }
  }

  /**
   * 生成缓存键
   */
  private getCacheKey(baseKey: string, options: UserQueryOptions): string {
    const parts = [baseKey]
    
    if (options.includePermissions) parts.push('perm')
    if (options.includePreferences) parts.push('pref')
    if (options.includeMetadata) parts.push('meta')
    
    return `user_${parts.join('_')}`
  }
}

/**
 * 用户仓储接口
 */
export interface IUserRepository {
  getCurrentUser(): Promise<UserData | null>
  getUserById(userId: string): Promise<UserData | null>
  updateCurrentUserPreferences(preferences: Partial<UserPreferences>): Promise<void>
}

/**
 * 权限服务接口
 */
export interface IPermissionService {
  getUserPermissions(userId: string): Promise<string[]>
}

/**
 * 缓存服务接口
 */
export interface ICacheService {
  get<T>(key: string): Promise<T | null>
  set<T>(key: string, value: T, ttl?: number): Promise<void>
  delete(key: string): Promise<void>
  clearByPrefix(prefix: string): Promise<void>
  clearByPattern(pattern: string): Promise<void>
}

/**
 * 用户数据接口
 */
interface UserData {
  id: string
  tenantId: string
  industryCode: string
  role: string
  username: string
  email?: string
  phone?: string
  avatar?: string
  preferences?: UserPreferences
  metadata?: Record<string, any>
  createdAt?: Date
  updatedAt?: Date
}